import {
  Container,
  TextInput,
  NumberInput,
  Button,
  Stack,
  Group,
  Text,
  Alert,
  Card,
  Title,
  Badge,
  Divider,
  Paper,
  Loader,
  Center,
  FileInput,
  ActionIcon,
  Table,
  Select,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconArrowLeft, IconDeviceFloppy, IconX, IconFile, IconCheck, IconDownload, IconEdit } from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bidSubmissionApi, CreateBidSubmissionDto, UpdateBidSubmissionDto } from '../../../api/bidSubmissions';
import { bidSubmissionDocumentsApi, BidSubmissionDocument } from '../../../api/bidSubmissionDocuments';
import { useVendorTenderRequirements } from '../hooks/useTenderRequirements';
import { useTenderItems } from '../../tenders/hooks/useTenderItems';
import { useVendorProfile } from '../hooks/useVendors';
import { useVendorDocuments } from '../hooks/useVendorDocuments';
import { useTenderRegistrationStatus } from '../hooks/useTenderRegistrations';
import { publicTenderApi } from '../../../api/publicTenders';
import { useTenderById } from '../../tenders/hooks/useTenders';
import { getCurrencySymbol, formatCurrency } from '../../../utils/currency';
import dayjs from 'dayjs';

export function VendorSubmissionDetail() {
  const { tenderId, submissionId } = useParams<{ tenderId: string; submissionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  
  const { data: vendorProfile } = useVendorProfile();
  const { data: vendorDocuments } = useVendorDocuments();
  const { data: tender } = useTenderById(tenderId!);
  const { data: publicTender } = useQuery({
    queryKey: ['public-tender-detail', tenderId],
    queryFn: () => publicTenderApi.getTenderById(tenderId!),
    enabled: !!tenderId,
  });
  const { data: registrationStatus } = useTenderRegistrationStatus(tenderId!);
  const { data: requirements, isLoading: loadingRequirements } = useVendorTenderRequirements(tenderId!);
  const { data: tenderItems, isLoading: loadingItems } = useTenderItems(tenderId!, true); // Use vendor endpoint

  // Fetch bid submission
  const { data: submission, isLoading: loadingSubmission, refetch: refetchSubmission } = useQuery({
    queryKey: ['bid-submission-detail', tenderId, submissionId],
    queryFn: () => bidSubmissionApi.getBidSubmissionById(tenderId!, submissionId!, true), // Use vendor auth
    enabled: !!tenderId && !!submissionId,
  });

  // Fetch uploaded documents for this bid submission
  const { data: uploadedDocuments, refetch: refetchDocuments, dataUpdatedAt } = useQuery({
    queryKey: ['bid-submission-documents', submission?.id],
    queryFn: async () => {
      // #region agent log
      if (typeof window !== 'undefined') {
        fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorSubmissionDetail.tsx:66',message:'Query function called',data:{submissionId:submission?.id},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
      }
      // #endregion
      const result = await bidSubmissionDocumentsApi.listDocuments(submission!.id);
      // #region agent log
      if (typeof window !== 'undefined') {
        fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorSubmissionDetail.tsx:72',message:'Query function returned',data:{submissionId:submission?.id,resultCount:result?.length,resultDocumentTypes:result?.map(d=>d.documentType)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
      }
      // #endregion
      return result;
    },
    enabled: !!submission?.id,
    refetchOnMount: true, // Always refetch when component mounts to ensure we have latest documents
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // #region agent log
  useEffect(() => {
    if (typeof window !== 'undefined') {
      fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorSubmissionDetail.tsx:84',message:'Documents query state changed',data:{submissionId:submission?.id,uploadedDocumentsCount:uploadedDocuments?.length,enabled:!!submission?.id,dataUpdatedAt,allDocumentTypes:uploadedDocuments?.map(d=>d.documentType)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
    }
  }, [uploadedDocuments, dataUpdatedAt]);
  // #endregion

  const isDraft = submission?.status === 'SAVED';
  const isSubmitted = submission?.status === 'SUBMITTED';

  // State for bid items (only editable if draft)
  const [bidItems, setBidItems] = useState<Array<{
    tenderItemId: string;
    itemName: string;
    description?: string;
    quantity: number;
    unit: string;
    price: number;
    order: number;
  }>>([]);

  // State for document selection (vendor documents)
  const [selectedDocuments, setSelectedDocuments] = useState<Record<string, string>>({});
  
  // State for file uploads (new files to upload)
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, File | null>>({});
  
  // State for uploading status
  const [uploadingDocuments, setUploadingDocuments] = useState<Record<string, boolean>>({});

  const form = useForm<CreateBidSubmissionDto>({
    initialValues: {
      companyName: '',
      totalPrice: 0,
      representativeName: '',
      representativeTitle: '',
      representativePhone: '',
      representativeEmail: '',
      companyAddress: '',
      registrationNumber: '',
      taxId: '',
      hasTOR: false,
      hasFinancialStatement: false,
      hasTaxClearance: false,
      hasInsurance: false,
      hasExperience: false,
      hasTechnicalProposal: false,
      hasFinancialProposal: false,
    },
  });

  // Initialize form and bid items from submission
  useEffect(() => {
    if (submission) {
      form.setValues({
        companyName: submission.companyName || '',
        totalPrice: Number(submission.totalPrice) || 0,
        representativeName: submission.representativeName || '',
        representativeTitle: submission.representativeTitle || '',
        representativePhone: submission.representativePhone || '',
        representativeEmail: submission.representativeEmail || '',
        companyAddress: (submission as any).companyAddress || '',
        registrationNumber: (submission as any).registrationNumber || '',
        taxId: (submission as any).taxId || '',
        hasTOR: submission.hasTOR || false,
        hasFinancialStatement: submission.hasFinancialStatement || false,
        hasTaxClearance: submission.hasTaxClearance || false,
        hasInsurance: submission.hasInsurance || false,
        hasExperience: submission.hasExperience || false,
        hasTechnicalProposal: submission.hasTechnicalProposal || false,
        hasFinancialProposal: submission.hasFinancialProposal || false,
      });

      // Initialize bid items from submission
      if (submission.bidItems && submission.bidItems.length > 0) {
        const items = submission.bidItems.map(item => ({
          tenderItemId: item.tenderItemId,
          itemName: item.tenderItem?.itemName || '',
          description: item.tenderItem?.description,
          quantity: item.quantity,
          unit: item.unit,
          price: Number(item.price),
          order: item.order,
        }));
        setBidItems(items);
      } else if (tenderItems && tenderItems.length > 0 && isDraft) {
        // Initialize from tender items if no bid items exist (draft only)
        const items = tenderItems.map(item => ({
          tenderItemId: item.id,
          itemName: item.itemName,
          description: item.description,
          quantity: item.quantity || 1,
          unit: item.unit || 'unit',
          price: 0,
          order: item.order,
        }));
        setBidItems(items);
      }
    }
  }, [submission, tenderItems, isDraft]);

  // Sync company info from vendor profile
  useEffect(() => {
    if (vendorProfile && isDraft) {
      form.setFieldValue('companyName', vendorProfile.businessName || '');
      form.setFieldValue('registrationNumber', vendorProfile.registrationNumber || '');
      form.setFieldValue('taxId', vendorProfile.taxId || '');
      form.setFieldValue('companyAddress', vendorProfile.address || '');
      if (!form.values.representativePhone) {
        form.setFieldValue('representativePhone', vendorProfile.phone || '');
      }
      if (!form.values.representativeEmail) {
        form.setFieldValue('representativeEmail', vendorProfile.email || '');
      }
    }
  }, [vendorProfile, isDraft]);

  const updateBidItem = (index: number, field: string, value: any) => {
    const updatedBidItems = [...bidItems];
    (updatedBidItems[index] as any)[field] = value;
    setBidItems(updatedBidItems);
  };

  // Function to upload a document for a requirement immediately
  const uploadDocumentForRequirement = async (requirementId: string, documentType: string, file?: File, vendorDocumentId?: string) => {
    // #region agent log
    if (typeof window !== 'undefined') {
      fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorSubmissionDetail.tsx:175',message:'Upload function called',data:{requirementId,documentType,hasFile:!!file,hasVendorDocId:!!vendorDocumentId,submissionId:submission?.id,uploadedDocumentsCountBefore:uploadedDocuments?.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
    }
    // #endregion

    if (!submission?.id) {
      notifications.show({
        title: 'Error',
        message: 'Bid submission not found. Please refresh the page.',
        color: 'red',
      });
      return;
    }

    setUploadingDocuments(prev => ({ ...prev, [requirementId]: true }));
    try {
      const formData = new FormData();
      formData.append('documentType', documentType);
      
      if (vendorDocumentId) {
        formData.append('vendorDocumentId', vendorDocumentId);
      } else if (file) {
        formData.append('file', file);
      } else {
        throw new Error('Either file or vendorDocumentId must be provided');
      }

      // #region agent log
      if (typeof window !== 'undefined') {
        fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorSubmissionDetail.tsx:198',message:'Before API upload call',data:{submissionId:submission.id,documentType},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
      }
      // #endregion

      // Optimistically update the query cache with a temporary document
      const tempDocument: BidSubmissionDocument = {
        id: `temp-${Date.now()}`,
        bidSubmissionId: submission.id,
        fileName: file?.name || 'Uploading...',
        fileSize: file?.size || 0,
        mimeType: file?.type || 'application/octet-stream',
        documentType,
        uploadedAt: new Date().toISOString(),
        status: 'PENDING',
      };

      // Optimistically add to cache
      queryClient.setQueryData<BidSubmissionDocument[]>(
        ['bid-submission-documents', submission.id],
        (old = []) => [...old, tempDocument]
      );

      let response;
      try {
        response = await bidSubmissionDocumentsApi.uploadDocument(submission.id, formData);
      } catch (apiError: any) {
        // #region agent log
        if (typeof window !== 'undefined') {
          fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorSubmissionDetail.tsx:240',message:'API upload call ERROR',data:{error:apiError?.message,responseStatus:apiError?.response?.status,responseData:apiError?.response?.data},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
        }
        // #endregion
        // Remove optimistic update on error
        queryClient.setQueryData<BidSubmissionDocument[]>(
          ['bid-submission-documents', submission.id],
          (old = []) => old.filter(doc => doc.id !== tempDocument.id)
        );
        throw apiError;
      }
      
      const document = response.data.document;
      
      // #region agent log
      if (typeof window !== 'undefined') {
        fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorSubmissionDetail.tsx:252',message:'After API upload call',data:{documentId:document?.id,documentType:document?.documentType,uploadedDocumentsCountBeforeRefetch:uploadedDocuments?.length,allDocumentTypes:uploadedDocuments?.map(d=>d.documentType)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
      }
      // #endregion
      
      // Clear the file/selection state
      setUploadedFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[requirementId];
        return newFiles;
      });
      setSelectedDocuments(prev => {
        const newDocs = { ...prev };
        delete newDocs[requirementId];
        return newDocs;
      });

      // Replace optimistic update with real document
      queryClient.setQueryData<BidSubmissionDocument[]>(
        ['bid-submission-documents', submission.id],
        (old = []) => old.map(doc => doc.id === tempDocument.id ? document : doc)
      );

      // #region agent log
      if (typeof window !== 'undefined') {
        fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorSubmissionDetail.tsx:275',message:'After optimistic update',data:{uploadedDocumentsCountAfterOptimistic:queryClient.getQueryData<BidSubmissionDocument[]>(['bid-submission-documents', submission.id])?.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
      }
      // #endregion

      // Invalidate and refetch to ensure we have the latest data
      await queryClient.invalidateQueries({ queryKey: ['bid-submission-documents', submission.id] });
      await refetchDocuments();
      
      // #region agent log
      if (typeof window !== 'undefined') {
        fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorSubmissionDetail.tsx:282',message:'After refetch',data:{uploadedDocumentsCountAfterRefetch:uploadedDocuments?.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
      }
      // #endregion

      notifications.show({
        title: 'Success',
        message: 'Document uploaded successfully',
        color: 'green',
      });
      
      return document;
    } catch (error: any) {
      console.error('Error uploading document:', error);
      notifications.show({
        title: 'Upload Error',
        message: error.response?.data?.error || 'Failed to upload document',
        color: 'red',
      });
      throw error;
    } finally {
      setUploadingDocuments(prev => ({ ...prev, [requirementId]: false }));
    }
  };

  const saveDraftMutation = useMutation({
    mutationFn: async (values: CreateBidSubmissionDto) => {
      const submissionData = {
        ...values,
        status: 'SAVED' as const,
        bidItems: bidItems.map(item => ({
          tenderItemId: item.tenderItemId,
          quantity: item.quantity,
          unit: item.unit,
          price: item.price,
          order: item.order,
        })),
      };
      return await bidSubmissionApi.updateBidSubmission(tenderId!, submissionId!, submissionData, true); // Use vendor auth
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bid-submission-detail', tenderId, submissionId] });
      queryClient.invalidateQueries({ queryKey: ['vendor-bid-submissions'] });
      notifications.show({
        title: 'Draft Saved',
        message: 'Your bid has been saved as draft.',
        color: 'blue',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to save draft. Please try again.',
        color: 'red',
      });
    },
  });

  const submitMutation = useMutation({
    mutationFn: async (values: CreateBidSubmissionDto) => {
      // Check registration requirement
      if (publicTender?.requireRegistration && registrationStatus?.registration?.status !== 'REGISTERED') {
        throw new Error('You must register for this tender before submitting a bid.');
      }

      if (!submission?.id) {
        throw new Error('Bid submission not found. Please refresh the page.');
      }

      // Validate that all required documents are uploaded
      if (requirements && requirements.length > 0) {
        const missingDocuments = requirements.filter(requirement => {
          const uploadedDoc = uploadedDocuments?.find(
            doc => doc.documentType === requirement.documentType
          );
          return !uploadedDoc;
        });

        if (missingDocuments.length > 0) {
          throw new Error(`Please upload all required documents. Missing: ${missingDocuments.map(r => getDocumentTypeDisplayName(r.documentType, r.description)).join(', ')}`);
        }
      }

      // Set all document flags to true since all required documents must be submitted
      const documentFlags: Record<string, boolean> = {};
      if (requirements && requirements.length > 0) {
        requirements.forEach(requirement => {
          const fieldName = `has${requirement.documentType === 'CUSTOM' ? 'Custom' : requirement.documentType.charAt(0) + requirement.documentType.slice(1).toLowerCase().replace(/_/g, '')}`;
          documentFlags[fieldName] = true;
        });
      }

      const submissionData = {
        ...values,
        ...documentFlags,
        status: 'SUBMITTED' as const,
        bidItems: bidItems.map(item => ({
          tenderItemId: item.tenderItemId,
          quantity: item.quantity,
          unit: item.unit,
          price: item.price,
          order: item.order,
        })),
      };
      
      // Update existing bid submission
      const updatedSubmission = await bidSubmissionApi.updateBidSubmission(
        tenderId!,
        submissionId!,
        submissionData
      );

      return updatedSubmission;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bid-submission-detail', tenderId, submissionId] });
      queryClient.invalidateQueries({ queryKey: ['vendor-bid-submissions'] });
      notifications.show({
        title: 'Success',
        message: 'Bid submitted successfully',
        color: 'green',
      });
      navigate(`/vendor/submissions`);
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || error.message || 'Failed to submit bid. Please try again.',
        color: 'red',
      });
    },
  });

  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    try {
      await saveDraftMutation.mutateAsync(form.values);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmit = async (values: CreateBidSubmissionDto) => {
    setIsSubmitting(true);
    try {
      await submitMutation.mutateAsync(values);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDocumentTypeDisplayName = (documentType: string, description?: string) => {
    if (documentType === 'CUSTOM' && description) {
      return description;
    }

    const displayNames: Record<string, string> = {
      'QUOTATION': 'Quotation',
      'COMPANY_PROFILE': 'Company Profile / Information',
      'REGISTRATION_CERTIFICATE': 'Company Registration Certificate',
      'GST_REGISTRATION': 'GST Registration',
      'EXPERIENCE_LETTERS': 'Experience Letters',
      'TOR': 'Terms of Reference',
      'FINANCIAL_STATEMENT': 'Financial Statement',
      'TAX_CLEARANCE': 'Tax Clearance',
      'INSURANCE': 'Insurance',
      'TECHNICAL_PROPOSAL': 'Technical Proposal',
      'FINANCIAL_PROPOSAL': 'Financial Proposal',
    };
    return displayNames[documentType] || documentType;
  };

  const getAvailableVendorDocuments = (documentType: string) => {
    if (!vendorDocuments) return [];
    
    const typeMapping: Record<string, string[]> = {
      'COMPANY_PROFILE': ['COMPANY_PROFILE'],
      'REGISTRATION_CERTIFICATE': ['BUSINESS_REGISTRATION'],
      'GST_REGISTRATION': ['TAX_CLEARANCE'],
      'TAX_CLEARANCE': ['TAX_CLEARANCE'],
      'INSURANCE': ['INSURANCE'],
      'FINANCIAL_STATEMENT': ['FINANCIAL_STATEMENT'],
      'EXPERIENCE_LETTERS': ['OTHER'],
    };
    
    const mappedTypes = typeMapping[documentType] || [];
    return vendorDocuments.filter(doc => mappedTypes.includes(doc.documentType));
  };

  if (loadingSubmission) {
    return (
      <Container size="xl" py="xl">
        <Center>
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  if (!submission) {
    return (
      <Container size="xl" py="xl">
        <Alert color="red" title="Not Found">
          Bid submission not found.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Paper shadow="sm" p="xl">
        <Group mb="xl">
          <Button
            variant="subtle"
            color="green"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/vendor/submissions')}
          >
            Back to Submissions
          </Button>
        </Group>

        <Title order={2} mb="md">
          Bid Submission Details
          {isDraft && (
            <Badge color="yellow" ml="md" leftSection={<IconEdit size={12} />}>
              Draft
            </Badge>
          )}
          {isSubmitted && (
            <Badge color="green" ml="md" leftSection={<IconCheck size={12} />}>
              Submitted
            </Badge>
          )}
        </Title>

        {publicTender && (
          <Text size="md" c="dimmed" fw={400} mb="md">
            {publicTender.referenceNo} - {publicTender.description}
          </Text>
        )}

        {isSubmitted && submission.submittedAt && (
          <Alert color="green" mb="md" icon={<IconCheck size={16} />}>
            <Text size="sm">
              This bid was submitted on {dayjs(submission.submittedAt).format('MMM DD, YYYY HH:mm')}.
              You cannot edit submitted bids.
            </Text>
          </Alert>
        )}

        {publicTender?.requireRegistration && registrationStatus?.registration?.status !== 'REGISTERED' && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md" title="Registration Required">
            You must register for this tender before submitting a bid. Please register first.
          </Alert>
        )}

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            {/* Company Information */}
            <Card shadow="sm" p="md">
              <Title order={4} mb="md">Company Information</Title>
              <Stack gap="md">
                <TextInput
                  label="Company Name"
                  placeholder="Enter company name"
                  required
                  readOnly={isSubmitted}
                  {...form.getInputProps('companyName')}
                />

                <Group grow>
                  <TextInput
                    label="Registration Number"
                    placeholder="Company registration number"
                    readOnly={isSubmitted}
                    {...form.getInputProps('registrationNumber')}
                  />
                  <TextInput
                    label="Tax ID"
                    placeholder="Tax identification number"
                    readOnly={isSubmitted}
                    {...form.getInputProps('taxId')}
                  />
                </Group>

                <TextInput
                  label="Company Address"
                  placeholder="Full company address"
                  readOnly={isSubmitted}
                  {...form.getInputProps('companyAddress')}
                />

                <Group grow>
                  <TextInput
                    label="Representative Name"
                    placeholder="Contact person name"
                    readOnly={isSubmitted}
                    {...form.getInputProps('representativeName')}
                  />
                  <TextInput
                    label="Representative Title"
                    placeholder="Job title"
                    readOnly={isSubmitted}
                    {...form.getInputProps('representativeTitle')}
                  />
                </Group>

                <Group grow>
                  <TextInput
                    label="Representative Phone"
                    placeholder="Phone number"
                    readOnly={isSubmitted}
                    {...form.getInputProps('representativePhone')}
                  />
                  <TextInput
                    label="Representative Email"
                    placeholder="Email address"
                    readOnly={isSubmitted}
                    {...form.getInputProps('representativeEmail')}
                  />
                </Group>
              </Stack>
            </Card>

            {/* Bid Items */}
            <Divider label="BID PRICING" labelPosition="center" />
            {loadingItems ? (
              <Center py="md">
                <Loader size="sm" />
              </Center>
            ) : bidItems.length > 0 ? (
              <Card shadow="sm" p="md">
                <Title order={4} mb="md">Bid Items</Title>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Item</Table.Th>
                      <Table.Th>Quantity</Table.Th>
                      <Table.Th>Unit</Table.Th>
                      <Table.Th>Unit Price</Table.Th>
                      <Table.Th>Subtotal</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {bidItems.map((item, index) => (
                      <Table.Tr key={item.tenderItemId}>
                        <Table.Td>
                          <div>
                            <Text fw={500}>{item.itemName}</Text>
                            {item.description && (
                              <Text size="sm" c="dimmed">{item.description}</Text>
                            )}
                          </div>
                        </Table.Td>
                        <Table.Td>
                          {isDraft ? (
                            <NumberInput
                              value={item.quantity}
                              onChange={(value) => updateBidItem(index, 'quantity', value || 1)}
                              min={1}
                              style={{ width: 100 }}
                            />
                          ) : (
                            <Text>{item.quantity}</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          {isDraft ? (
                            <TextInput
                              value={item.unit}
                              onChange={(e) => updateBidItem(index, 'unit', e.target.value)}
                              style={{ width: 100 }}
                            />
                          ) : (
                            <Text>{item.unit}</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          {isDraft ? (
                            <NumberInput
                              value={item.price}
                              onChange={(value) => updateBidItem(index, 'price', value || 0)}
                              min={0}
                              decimalScale={2}
                              prefix={tender?.currency ? getCurrencySymbol(tender.currency) : 'Rf'}
                              style={{ width: 150 }}
                            />
                          ) : (
                            <Text>{tender?.currency ? formatCurrency(Number(item.price), tender.currency) : `Rf${Number(item.price).toFixed(2)}`}</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text fw={500}>
                            {tender?.currency ? formatCurrency(item.price * item.quantity, tender.currency) : `Rf${(item.price * item.quantity).toFixed(2)}`}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Card>
            ) : (
              <Text size="sm" c="dimmed">No bid items defined.</Text>
            )}

            <NumberInput
              label="Total Bid Price"
              placeholder="Enter total bid price"
              required
              min={0}
              decimalScale={2}
              prefix={tender?.currency ? getCurrencySymbol(tender.currency) : 'Rf'}
              readOnly={isSubmitted}
              {...form.getInputProps('totalPrice')}
            />

            {/* Document Requirements */}
            <Divider label="REQUIRED DOCUMENTS" labelPosition="center" />
            {loadingRequirements ? (
              <Center py="md">
                <Loader size="sm" />
              </Center>
            ) : requirements && requirements.length > 0 ? (
              <Card shadow="sm" p="md">
                <Title order={4} mb="md">Document Requirements</Title>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Document Type</Table.Th>
                      <Table.Th>File Name</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {requirements.map((requirement) => {
                      const availableDocs = getAvailableVendorDocuments(requirement.documentType);
                      const hasVendorDocs = availableDocs.length > 0;
                      
                      // Check if document is already uploaded for this requirement
                      const uploadedDoc = uploadedDocuments?.find(
                        doc => doc.documentType === requirement.documentType
                      );

                      // #region agent log
                      if (typeof window !== 'undefined') {
                        fetch('http://127.0.0.1:7242/ingest/7c805428-0cbf-4a21-923a-5d22bb0a3d78',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'VendorSubmissionDetail.tsx:743',message:'Rendering requirement',data:{requirementId:requirement.id,documentType:requirement.documentType,uploadedDocumentsCount:uploadedDocuments?.length,hasUploadedDoc:!!uploadedDoc,uploadedDocId:uploadedDoc?.id,uploadedDocType:uploadedDoc?.documentType,allDocumentTypes:uploadedDocuments?.map(d=>d.documentType)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
                      }
                      // #endregion

                      return (
                        <Table.Tr key={requirement.id}>
                          <Table.Td>
                            <div>
                              <Text size="sm" fw={500}>
                                {getDocumentTypeDisplayName(requirement.documentType, requirement.description)}
                              </Text>
                              {requirement.description && requirement.documentType !== 'CUSTOM' && (
                                <Text size="xs" c="dimmed">
                                  {requirement.description}
                                </Text>
                              )}
                            </div>
                          </Table.Td>
                          <Table.Td>
                            {uploadedDoc ? (
                              <Group gap="xs">
                                <IconFile size={14} />
                                <Text size="sm">{uploadedDoc.fileName}</Text>
                              </Group>
                            ) : (
                              <Text size="sm" c="dimmed">-</Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            {uploadedDoc ? (
                              <Group gap="xs">
                                <ActionIcon
                                  size="sm"
                                  color="blue"
                                  variant="light"
                                  onClick={async () => {
                                    try {
                                      const response = await bidSubmissionDocumentsApi.downloadDocument(uploadedDoc.id, true); // Use vendor auth
                                      // Create a blob URL and trigger download
                                      const url = window.URL.createObjectURL(new Blob([response.data]));
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.setAttribute('download', uploadedDoc.fileName);
                                      document.body.appendChild(link);
                                      link.click();
                                      link.remove();
                                      window.URL.revokeObjectURL(url);
                                    } catch (error: any) {
                                      notifications.show({
                                        title: 'Error',
                                        message: error.response?.data?.error || 'Failed to download document',
                                        color: 'red',
                                      });
                                    }
                                  }}
                                  title="Download document"
                                >
                                  <IconDownload size={14} />
                                </ActionIcon>
                                {isDraft && (
                                  <ActionIcon
                                    size="sm"
                                    color="red"
                                    variant="light"
                                    onClick={async () => {
                                      try {
                                        await bidSubmissionDocumentsApi.removeDocument(uploadedDoc.id);
                                        await queryClient.invalidateQueries({ queryKey: ['bid-submission-documents', submission?.id] });
                                        await refetchDocuments();
                                        notifications.show({
                                          title: 'Success',
                                          message: 'Document removed',
                                          color: 'green',
                                        });
                                      } catch (error: any) {
                                        notifications.show({
                                          title: 'Error',
                                          message: error.response?.data?.error || 'Failed to remove document',
                                          color: 'red',
                                        });
                                      }
                                    }}
                                    title="Remove document"
                                  >
                                    <IconX size={14} />
                                  </ActionIcon>
                                )}
                              </Group>
                            ) : isDraft ? (
                              <Stack gap="xs">
                                {hasVendorDocs && (
                                  <Select
                                    placeholder="Select from profile"
                                    size="xs"
                                    data={[
                                      { value: '', label: 'Upload new' },
                                      ...availableDocs.map(doc => ({
                                        value: doc.id,
                                        label: doc.fileName.length > 30 ? `${doc.fileName.substring(0, 30)}...` : doc.fileName,
                                      })),
                                    ]}
                                    value={selectedDocuments[requirement.id] || ''}
                                    onChange={async (value) => {
                                      if (value && submission?.id) {
                                        await uploadDocumentForRequirement(
                                          requirement.id,
                                          requirement.documentType,
                                          undefined,
                                          value
                                        );
                                      } else {
                                        setSelectedDocuments({
                                          ...selectedDocuments,
                                          [requirement.id]: value || '',
                                        });
                                      }
                                    }}
                                    disabled={uploadingDocuments[requirement.id]}
                                    style={{ width: 200 }}
                                  />
                                )}
                                
                                {(!selectedDocuments[requirement.id] || !hasVendorDocs) && (
                                  <FileInput
                                    placeholder="Choose file"
                                    size="xs"
                                    required={!selectedDocuments[requirement.id]}
                                    value={uploadedFiles[requirement.id] || null}
                                    onChange={async (file) => {
                                      if (file && submission?.id) {
                                        await uploadDocumentForRequirement(
                                          requirement.id,
                                          requirement.documentType,
                                          file
                                        );
                                      } else {
                                        setUploadedFiles(prev => ({
                                          ...prev,
                                          [requirement.id]: file,
                                        }));
                                      }
                                    }}
                                    disabled={uploadingDocuments[requirement.id]}
                                    leftSection={<IconFile size={14} />}
                                    accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                                    style={{ width: 200 }}
                                  />
                                )}
                                
                                {uploadingDocuments[requirement.id] && (
                                  <Text size="xs" c="dimmed">
                                    Uploading...
                                  </Text>
                                )}
                              </Stack>
                            ) : (
                              <Text size="sm" c="dimmed">-</Text>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Card>
            ) : (
              <Text size="sm" c="dimmed">No document requirements defined for this tender.</Text>
            )}

            {/* Action Buttons */}
            {isDraft && (
              <Group justify="space-between" mt="md">
                <Button
                  variant="light"
                  leftSection={<IconDeviceFloppy size={16} />}
                  onClick={handleSaveDraft}
                  loading={isSavingDraft}
                    disabled={publicTender?.requireRegistration && registrationStatus?.registration?.status !== 'REGISTERED'}
                >
                  Save as Draft
                </Button>
                <Group>
                  <Button variant="light" onClick={() => navigate('/vendor/submissions')}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    color="green"
                    loading={isSubmitting}
                    disabled={publicTender?.requireRegistration && (registrationStatus as any)?.status !== 'REGISTERED'}
                  >
                    Submit Bid
                  </Button>
                </Group>
              </Group>
            )}
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}

